import { superstructResolver } from "@hookform/resolvers/superstruct";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Paper, Stack, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { boolean, Infer, string, type } from "superstruct";
import { handleError } from "../../Common/helpers/handleError";
import { FullHeightContainer } from "../../Common/views/FullHeightContainer";
import { LoadingView } from "../../Common/views/LoadingView";
import { ControlledSwitch } from "../../Form/views/ControlledSwitch";
import { ControlledTextField } from "../../Form/views/ControlledTextField";
import { useT } from "../../Translation/hooks/useT";
import { deleteWebhookConfigCallable } from "../callables/deleteWebhookConfigCallable";
import { getWebhookConfigCallable } from "../callables/getWebhookConfigCallable";
import { upsertWebhookConfigCallable } from "../callables/upsertWebhookConfigCallable";

const FormValuesStruct = type({
  url: string(),
  bearerToken: string(),
  enabled: boolean(),
});

type FormValues = Infer<typeof FormValuesStruct>;

export const WebhookConfigPage: FC = () => {
  const t = useT();
  const { organizationId } = useParams<"organizationId">();
  const [isLoading, setIsLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: superstructResolver(FormValuesStruct),
    defaultValues: {
      url: "",
      bearerToken: "",
      enabled: true,
    },
  });

  const loadConfig = useCallback(async () => {
    if (!organizationId) return;

    try {
      setIsLoading(true);
      const response = await getWebhookConfigCallable({ organizationId });
      const hasConfig = !!response.url;
      setIsConfigured(hasConfig);
      if (hasConfig) {
        reset({
          url: response.url || "",
          bearerToken: response.bearerToken || "",
          enabled: response.enabled ?? true,
        });
      }
    } catch (error) {
      handleError(error).logAnd().toast();
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, reset]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const onSubmit = async (formValues: FormValues) => {
    if (!organizationId) return;

    try {
      await upsertWebhookConfigCallable({
        organizationId,
        url: formValues.url,
        bearerToken: formValues.bearerToken,
        enabled: formValues.enabled,
      });

      setIsConfigured(true);
      toast.success(t("WebhookConfigPage_Success_Saved"));
    } catch (error) {
      handleError(error).logAnd().toast();
    }
  };

  const handleDelete = async () => {
    if (!organizationId) return;

    if (!window.confirm(t("WebhookConfigPage_Text_ConfirmDelete"))) {
      return;
    }

    try {
      await deleteWebhookConfigCallable({ organizationId });
      setIsConfigured(false);
      reset({ url: "", bearerToken: "", enabled: true });
      toast.success(t("WebhookConfigPage_Success_Deleted"));
    } catch (error) {
      handleError(error).logAnd().toast();
    }
  };

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <FullHeightContainer maxWidth="sm">
      <Stack py={5} spacing={3}>
        <Typography variant="h2">
          {t("WebhookConfigPage_Title_Configuration")}
        </Typography>

        <Typography variant="body1" color="text.secondary">
          {t("WebhookConfigPage_Text_Description")}
        </Typography>

        <Paper>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3} p={3}>
              <ControlledTextField
                required
                fullWidth
                name="url"
                label={t("WebhookConfigPage_Field_Url")}
                placeholder="https://your-system.com/webhook"
                control={control}
                disabled={isSubmitting}
              />

              <ControlledTextField
                required
                fullWidth
                name="bearerToken"
                label={t("WebhookConfigPage_Field_BearerToken")}
                control={control}
                disabled={isSubmitting}
              />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography>{t("WebhookConfigPage_Field_Enabled")}</Typography>
                <ControlledSwitch
                  label=""
                  name="enabled"
                  control={control}
                  disabled={isSubmitting}
                />
              </Stack>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {isConfigured && (
                  <Button
                    variant="text"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                    disabled={isSubmitting}
                  >
                    {t("WebhookConfigPage_Button_Delete")}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {t("Common_Save")}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </FullHeightContainer>
  );
};
